import * as Phaser from 'phaser';
import { BALANCE } from '@/config/balance';
import { ElephantStage, GAME_EVENTS, type Lane, type StageChangedPayload } from '@/types';
import { TEXTURE_KEYS } from '@/utils/textures';
import { applyHit, applyProgress } from '@/utils/progression';

export interface SetStageOptions {
  instant?: boolean;
}

export class Elephant extends Phaser.Physics.Arcade.Image {
  private currentStage: ElephantStage = ElephantStage.TINY;
  private currentLane: Lane = 1;
  private currentProgress = 0;
  private invincible = false;
  private scaleTween?: Phaser.Tweens.Tween;
  private laneTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, TEXTURE_KEYS.elephant);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setOrigin(0.5, 0.5);
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    body?.setAllowGravity(false);
    body?.setImmovable(true);
    this.setStage(BALANCE.elephant.startStage as ElephantStage, { instant: true });
  }

  getStage(): ElephantStage {
    return this.currentStage;
  }

  setStage(rawStage: number, opts: SetStageOptions = {}): void {
    const clamped = Phaser.Math.Clamp(
      Math.round(rawStage),
      0,
      BALANCE.elephant.maxStage,
    ) as ElephantStage;
    const targetScale = BALANCE.elephant.scales[clamped];
    if (targetScale === undefined) return;

    this.currentStage = clamped;
    this.applyHitbox(clamped);

    if (opts.instant) {
      this.scaleTween?.stop();
      this.scaleTween = undefined;
      this.setScale(targetScale);
      return;
    }

    this.scaleTween?.stop();
    this.scaleTween = this.scene.tweens.add({
      targets: this,
      scale: targetScale,
      duration: BALANCE.elephant.levelUpAnimDurationMs,
      ease: 'Back.easeOut',
    });
  }

  getScoreMultiplier(): number {
    return BALANCE.elephant.multipliers[this.currentStage] ?? 1;
  }

  getProgress(): number {
    return this.currentProgress;
  }

  setProgress(value: number): void {
    this.currentProgress = Math.max(0, value);
  }

  addProgress(value: number): void {
    const previousStage = this.currentStage;
    const result = applyProgress(
      this.currentStage,
      this.currentProgress,
      value,
      BALANCE.elephant.maxStage,
      BALANCE.elephant.progressPerStage,
    );
    this.currentProgress = result.progress;
    this.scene.events.emit(GAME_EVENTS.ProgressChanged, this.currentProgress);
    if (result.stage !== this.currentStage) {
      this.setStage(result.stage);
      const payload: StageChangedPayload = {
        stage: this.currentStage,
        previousStage,
        reason: 'levelup',
      };
      this.scene.events.emit(GAME_EVENTS.StageChanged, payload);
      this.playLevelUpFx();
    }
  }

  private playLevelUpFx(): void {
    this.setTint(0xffffff).setTintFill();
    this.scene.time.delayedCall(140, () => this.clearTint());
  }

  playCollectPunch(): void {
    // Skip nếu đang trong stage scale tween (Back.easeOut overshooting) — tránh đụng tween
    if (this.scaleTween && this.scaleTween.isPlaying()) return;
    const base = BALANCE.elephant.scales[this.currentStage];
    if (base === undefined) return;
    this.scene.tweens.add({
      targets: this,
      scale: base * 1.08,
      duration: 90,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
  }

  isInvincible(): boolean {
    return this.invincible;
  }

  setInvincible(value: boolean): void {
    this.invincible = value;
  }

  takeHit(): { gameOver: boolean; previousStage: ElephantStage; newStage: ElephantStage } {
    const previous = this.currentStage;
    const result = applyHit(previous, BALANCE.elephant.progressOnHit);
    this.playHurtFx();
    if (result.gameOver) {
      return { gameOver: true, previousStage: previous, newStage: previous };
    }
    const newStage = result.stage as ElephantStage;
    this.currentStage = newStage;
    this.currentProgress = result.progress;
    this.setStage(newStage);
    const payload: StageChangedPayload = {
      stage: newStage,
      previousStage: previous,
      reason: 'hit',
    };
    this.scene.events.emit(GAME_EVENTS.StageChanged, payload);
    this.scene.events.emit(GAME_EVENTS.ProgressChanged, this.currentProgress);
    return { gameOver: false, previousStage: previous, newStage };
  }

  private playHurtFx(): void {
    this.setTint(0xff5050).setTintFill();
    this.scene.time.delayedCall(220, () => this.clearTint());
  }

  getLane(): Lane {
    return this.currentLane;
  }

  shiftLane(dir: -1 | 1): void {
    const raw = this.currentLane + dir;
    const max = BALANCE.lanes.count - 1;
    if (raw < 0 || raw > max) return;
    this.moveToLane(raw as Lane);
  }

  moveToLane(lane: Lane): void {
    const targetX = BALANCE.lanes.positions[lane];
    if (targetX === undefined) return;
    this.currentLane = lane;
    this.laneTween?.stop();
    this.laneTween = this.scene.tweens.add({
      targets: this,
      x: targetX,
      duration: BALANCE.lanes.laneChangeAnimDurationMs,
      ease: 'Quad.easeOut',
    });
  }

  private applyHitbox(stage: ElephantStage): void {
    const size = BALANCE.elephant.hitboxSizes[stage];
    if (size === undefined) return;
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    body?.setSize(size, size, true);
  }
}
